import { useCallback, useEffect } from 'react';
import { isEmpty } from 'lodash-es';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarGridCreateEvent } from '../../types';

const MINUTES_SNAP_INCREMENT = 5;

const getRoundOffMinutes = (totalMinutes: number, increment = MINUTES_SNAP_INCREMENT) => {
  return Math.round(totalMinutes / increment) * increment;
};

type UseDragCreateSelectionOverlayOptions = {
  isAllowDragCreateSelection: boolean;
  onEventCreate?: (selection: CalendarGridCreateEvent) => void;
};

export const useDragCreateSelectionOverlay = ({
  isAllowDragCreateSelection,
  onEventCreate,
}: UseDragCreateSelectionOverlayOptions) => {
  const { dragCreateSelection, setDragCreateSelection } = useCalendarGridStore([
    'dragCreateSelection',
    'setDragCreateSelection',
  ]);

  const isDragCreateSelectionStarted = isAllowDragCreateSelection && !isEmpty(dragCreateSelection);

  const clearSelection = useCallback(() => {
    setDragCreateSelection(undefined);
  }, [setDragCreateSelection]);

  const startDragCreateSelection = useCallback(
    (columnId?: string, groupId?: string, totalMinutes?: number) => {
      if (!isAllowDragCreateSelection || !columnId || !groupId || totalMinutes === undefined) return;

      const roundOffMinutes = getRoundOffMinutes(totalMinutes);
      setDragCreateSelection({
        columnId,
        groupId,
        startMinutes: roundOffMinutes,
        endMinutes: roundOffMinutes,
      });
    },
    [isAllowDragCreateSelection, setDragCreateSelection]
  );

  const progressDragCreateSelection = useCallback(
    (totalMinutes?: number) => {
      if (!isDragCreateSelectionStarted || totalMinutes === undefined) return;

      const roundOffMinutes = getRoundOffMinutes(totalMinutes);
      if (dragCreateSelection.endMinutes === roundOffMinutes) {
        return;
      }

      setDragCreateSelection({
        ...dragCreateSelection,
        endMinutes: roundOffMinutes,
      });
    },
    [dragCreateSelection, isDragCreateSelectionStarted, setDragCreateSelection]
  );

  const endDragCreateSelection = useCallback(() => {
    if (!isDragCreateSelectionStarted) return;

    const { columnId, groupId, startMinutes, endMinutes } = dragCreateSelection;
    const isReversed = startMinutes > endMinutes;

    onEventCreate?.({
      columnId,
      groupId,
      startMinutes: isReversed ? endMinutes : startMinutes,
      endMinutes: isReversed ? startMinutes : endMinutes,
    });

    clearSelection();
  }, [clearSelection, dragCreateSelection, isDragCreateSelectionStarted, onEventCreate]);

  useEffect(() => {
    return () => clearSelection();
  }, [clearSelection]);

  return {
    isDragCreateSelectionStarted,
    startDragCreateSelection,
    progressDragCreateSelection,
    endDragCreateSelection,
  };
};
